import { CustomProvider, FinalValueResolver, ValueResolver } from '@philips/odin';

import { DataContext, SubmittableFragmentContext } from '@philips-emr/tws-core/lib/component/context';
import { HistoryService, HttpService, MessageService } from '@philips-emr/tws-core/lib/services';

import FragmentNode from '../fragment-node';

import MetadataTransverser from './metadata-tranverser';

export interface FragmentCustomProviderArgs {
  dataContext: DataContext;
  fragment: FragmentNode;
  submittableFragmentContext: SubmittableFragmentContext | null;
}

/**
 * Custom DI Provider for Engine resources.
 * It provides the core services, and components APIs.
 */
class FragmentCustomProvider extends CustomProvider {

  private context: DataContext;
  private submittableFragmentContext: SubmittableFragmentContext | null;
  private fragment: FragmentNode;
  private metadataTransverser: MetadataTransverser;

  private injects: {
    [k: string]: object;
  } = {
    HistoryService,
    HttpService,
    MessageService,
  };

  constructor(args: FragmentCustomProviderArgs) {
    super();
    this.context = args.dataContext;
    this.fragment = args.fragment;
    this.submittableFragmentContext = args.submittableFragmentContext;

    this.metadataTransverser = new MetadataTransverser(args.fragment.getMetadata());
  }

  resolve(identifier: string): object {

    if (identifier === 'Context') {
      return this.createDataContextResolver();
    }

    if (identifier === 'SubmittableFragmentContext') {
      return this.createSubmittableFragmentContextResolver();
    }

    const service = this.injects[identifier];
    if (service) {
      return new FinalValueResolver(() => {
        return service;
      });
    }

    const uuid = this.metadataTransverser.getUuid(identifier);

    if (uuid) {
      return new ValueResolver(() => {
        const component = this.fragment.getChild(uuid);
        if (component) {
          return component.getAPI();
        }
        throw new Error('You should not access not rendered component apis.');
      });
    }

    return super.resolve(identifier);
  }

  createDataContextResolver(): object {
    return new FinalValueResolver(() => {
      return this.context;
    });
  }

  createSubmittableFragmentContextResolver(): object {
    return new FinalValueResolver(() => {
      if (this.submittableFragmentContext) {
        return this.submittableFragmentContext;
      }

      throw new Error('You should not access SubmittableFragmentContext outside a SubmittableFragment.');
    });
  }

}

export default FragmentCustomProvider;
